import React from "react";

import { Dimensions, Measure } from "./Measure";

const ReactList = require("react-list");

export type ListType = "simple" | "uniform" | "variable";

export type JSXCallback = () => JSX.Element;
export type EmptyReturn = JSX.Element | JSXCallback;

export interface ListProps {
    /**
     * The length of the list
     */
    length: number;
    /**
     * The type of list
     */
    type?: ListType;
    /**
     * An element to display when the list is empty
     */
    onEmpty?: EmptyReturn;
    /**
     * Reduces the dynamic height by the passed value. Height is set to 100% if there is no value
     */
    reduceHeight?: number;
    /**
     * Renders the item
     * @param index
     * @param key
     */
    itemRenderer(index: number, key: string): JSX.Element;
    /**
     * Callback when a list item is selected
     * @param index
     */
    onSelect?(index: number): void;
    /**
     * Optional callback when the user scrolls through the list
     * @param firstVisibleIndex
     * @param lastVisibleIndex
     * @param total
     */
    onScroll?(firstVisibleIndex: number, lastVisibleIndex: number, total: number): void;
}

interface ListState {
    dimens: Dimensions;
}

/**
 * A list that measures out it's own height so it does not stretch beyond the visible area.
 * Items will not render beyond the visible height until the user scrolls to them. This prevents, say,
 * a thousand items being rendered all at once.
 *
 * The ItemRenderer should return a ListItem component.
 *
 * This is a wrapper around ReactList
 */
export class StaticList extends React.Component<ListProps, ListState> {
    static defaultProps = {
        onEmpty: "The list is empty.",
        type: "uniform"
    };

    list: any;

    constructor(props: ListProps) {
        super(props);
        this.state = {
            dimens: undefined
        };

        this.updateDimensions = this.updateDimensions.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleListRef = this.handleListRef.bind(this);
        this.handleEmpty = this.handleEmpty.bind(this);
    }

    updateDimensions(dimensions: Dimensions) {
        this.setState({ dimens: dimensions });
    }

    handleScroll(event: React.UIEvent<object>) {
        const visibleRange: number[] = this.list.getVisibleRange();
        if (this.props.onScroll) {
            this.props.onScroll(visibleRange[0], visibleRange[1], this.props.length);
        }
    }

    handleListRef(list: Element) {
        this.list = list;
    }

    handleEmpty(): JSX.Element {
        const { length, onEmpty } = this.props;
        if (length === 0) {
            if (typeof onEmpty === "function") {
                return onEmpty();
            }
            return onEmpty;
        }
        return <></>;
    }

    render(): JSX.Element {
        let parentStyle: React.CSSProperties = {};
        const reduceHeight = this.props.reduceHeight;

        if (this.state.dimens) {
            parentStyle = {
                height: reduceHeight ? this.state.dimens.height - reduceHeight : "100%",
                overflowY: "auto"
            };
        }

        return (
            <Measure onMeasure={this.updateDimensions}>
                {this.props.length === 0 ?
                    this.handleEmpty()
                    :
                    <div style={parentStyle} onScroll={this.handleScroll}>
                        <ReactList
                            ref={this.handleListRef}
                            itemRenderer={this.props.itemRenderer}
                            length={this.props.length}
                            type={this.props.type}
                        />
                    </div>}
            </Measure>
        );
    }
}
